import { useSubmittedUserMetrics, useSubmitUserMetricsMutation } from 'api/user_metrics';
import { getMetricaAttribution, getMetricaClientId, initializeMetrica, setMetricaUserId } from 'utils/metrica';
import React, { ReactNode, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import {getToken, useGetUserMutation, useUpdateUserInfoMutation} from "../../../api/auth";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {ROUTES} from "../../../config/constants";
import {changeAppLanguage, getLanguageByIp, getStoredAppLanguage} from "../../../config/i18";
import {isDemoPage} from "../../../utils/hooks";

type AuthProviderProps = {
    children: ReactNode
}

const AuthProvider = ({ children } : AuthProviderProps) => {

  const [ userData, setUserData ] = useState(undefined);
  const [ isSuccess, setIsSuccess ] = useState(false);
  const [ isNewUser, setIsNewUser ] = useState(false);

  const [ getUser ] = useGetUserMutation()
  const [ updateUserInfo ] = useUpdateUserInfoMutation()

  const [ yandexId, setYandexId ] = useState(getMetricaClientId);
  const submittedMetrics = useSubmittedUserMetrics();
  const [ submitUserMetrics, metricsRequest ] = useSubmitUserMetricsMutation({ fixedCacheKey: 'userMetrics' });

  const authToken = getToken();

  const navigate = useNavigate();

  const isDemo = isDemoPage();

  useEffect(() => {
    let active = true;

    initializeMetrica().then((value) => {
      if (active) setYandexId(value);
    });

    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (isDemo || !yandexId) return;
    if (authToken) return;
    if (metricsRequest.isLoading || metricsRequest.isError) return;

    const submission: UserMetricsInput = {
      ...getMetricaAttribution(),
      client_id: yandexId,
    };
    const alreadySubmitted = JSON.stringify(submittedMetrics) === JSON.stringify(submission);

    if (alreadySubmitted) return;

    const timer = window.setTimeout(() => {
      if (!getToken()) submitUserMetrics(submission);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [ authToken, yandexId, isDemo, metricsRequest.isLoading, metricsRequest.isError, submittedMetrics, submitUserMetrics ]);

  const handleUser = (user: any) => {
    if (!user) {
      setUserData(undefined);
      return;
    }

    const storedLanguage = getStoredAppLanguage();

    if (isDemo && storedLanguage) {
      changeAppLanguage(storedLanguage);
      setUserData(user);
      return;
    }

    if (user?.lang) {
      changeAppLanguage(user.lang);
      setUserData(user);
      return;
    }

    if (storedLanguage) {
      const language = changeAppLanguage(storedLanguage);
      setUserData({ ...user, lang: language.toUpperCase() });
      return;
    }

    setUserData(user);
  };

  const updateUserLanguage = async (user: any) => {
    const lang = getStoredAppLanguage() || await getLanguageByIp();
    const userLang = lang.toUpperCase();

    changeAppLanguage(lang);

    return updateUserInfo({ lang: userLang }).then((result) => {
      // @ts-ignore
      if (result?.data) {
        return {
          ...user,
          // @ts-ignore
          ...result.data,
        };
      }

      return {
        ...user,
        lang: userLang,
      };
    });
  }

  const fetchLoggedInUser = async () => {

    try {
      // @ts-ignore
      const response = await getUser()

      if (!getToken()) {
        return;
      }

      if ('error' in response || !response.data) {
        setIsNewUser(true);
        if (!isDemoPage()) {
          navigate(ROUTES.LOGIN)
        }
        return
      }

      setMetricaUserId(response.data.id);

      const shouldUpdateUserLanguage = !isDemo && response.data?.lang === null;
      const user = shouldUpdateUserLanguage ? await updateUserLanguage(response.data) : response.data;

      if (!getToken()) {
        return;
      }

      // @ts-ignore
      handleUser(user);
      setIsNewUser(false)
    } catch (error) {
      console.error(error);
    } finally {
      setIsSuccess(true)
    }
  };

  const fetchNewUserLanguage = async () => {
    try {
      const storedLanguage = getStoredAppLanguage();

      if (storedLanguage) {
        changeAppLanguage(storedLanguage);
        return;
      }

      const lang = await getLanguageByIp();

      changeAppLanguage(lang);
    } catch (error) {
      console.error(error);
    } finally {
      setIsNewUser(true);
      setIsSuccess(true)
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setIsSuccess(false);

      if (isDemo) {
        const storedLanguage = getStoredAppLanguage();

        if (storedLanguage) {
          changeAppLanguage(storedLanguage);
        }

        if (authToken) {
          fetchLoggedInUser();
        } else {
          setIsNewUser(true);
          setIsSuccess(true);
        }
        return;
      }

      if (authToken) {
        fetchLoggedInUser();
      } else {
        fetchNewUserLanguage();
      }
    }, 0);

    return () => window.clearTimeout(timer);
  }, [ authToken, isDemo ])

  return (
    <AuthContext.Provider
      value={{ user: userData, setUser: handleUser, isSuccess, isNewUser, setIsSuccess }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
